/**
 * ReviewNudgeModal — "love it / hate it" deflector shown after a user's
 * 5th completed round.
 *
 * Why: a classic conversion pattern that lifts Play Store / App Store ratings
 * by routing unhappy players to a private support ticket BEFORE they reach
 * a public review surface. Three paths:
 *
 *   1. "Loving it ⭐"        → Play Store / App Store / share link
 *   2. "Tell me what's wrong" → in-app support form (private)
 *   3. "Maybe later"          → snooze for 30 days
 *
 * Trigger lives in `<RoundSummary>`. State is per-device via localStorage:
 *   - `reviewNudgeDismissed`  → never show again (after happy or angry path)
 *   - `reviewNudgeSnoozeUntil` → millis timestamp; suppresses until that date
 */
import { Link } from "react-router-dom";
import { Star, EnvelopeSimple, Clock, X, ShareNetwork } from "@phosphor-icons/react";

const DISMISS_KEY = "reviewNudgeDismissed";
const SNOOZE_KEY = "reviewNudgeSnoozeUntil";
const SNOOZE_DAYS = 30;

// Set these once the app is listed. Leaving them empty falls back to the
// Web Share API (or copy-link) so the "Loving it" CTA still does something
// useful even pre-launch.
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "";
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || "";
const WEB_SHARE_URL = "https://fairway-golf.org/?ref=share";

/** Should we show the nudge for a user with this many completed rounds? */
export function shouldShowReviewNudge(completedRounds) {
  if ((completedRounds ?? 0) < 5) return false;
  try {
    if (localStorage.getItem(DISMISS_KEY) === "1") return false;
    const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozeUntil && Date.now() < snoozeUntil) return false;
  } catch { /* localStorage unavailable → show it */ }
  return true;
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, "1"); } catch (_e) { /* no-op */ }
}
function markSnoozed() {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
  } catch (_e) { /* no-op */ }
}

export default function ReviewNudgeModal({ onClose }) {
  // Detect which store CTA to surface based on user agent — keeps the
  // primary button single + clear instead of a confusing two-button row.
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isAndroid = /android/i.test(ua);
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const storeUrl = isIOS && APP_STORE_URL
    ? APP_STORE_URL
    : isAndroid && PLAY_STORE_URL
      ? PLAY_STORE_URL
      : "";

  const onLoveIt = async () => {
    markDismissed();
    if (storeUrl) {
      window.open(storeUrl, "_blank", "noopener,noreferrer");
    } else if (navigator.share) {
      // Pre-launch / web users — turn the positive intent into a share with a mate.
      try {
        await navigator.share({
          title: "Fairway Golf",
          text: "Cracking little golf tracker — give it a go",
          url: WEB_SHARE_URL,
        });
      } catch (err) {
        if (err && err.name !== "AbortError") {
          console.warn("ReviewNudge: share failed", err);
        }
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(WEB_SHARE_URL);
        window.alert("Link copied — share Fairway with a mate. Cheers!");
      } catch (err) {
        console.warn("ReviewNudge: clipboard failed", err);
      }
    }
    onClose?.();
  };

  const onSnooze = () => {
    markSnoozed();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-nudge-title"
      data-testid="review-nudge"
      onClick={(e) => { if (e.target === e.currentTarget) onSnooze(); }}
    >
      <div className="bg-[var(--bg-card)] rounded-3xl w-full sm:max-w-sm shadow-2xl overflow-hidden flex flex-col">
        {/* Close (snoozes) */}
        <button
          type="button"
          onClick={onSnooze}
          aria-label="Maybe later"
          data-testid="review-nudge-close"
          className="absolute top-4 right-4 h-8 w-8 rounded-full bg-black/15 hover:bg-black/25 text-white flex items-center justify-center transition-colors z-10"
        >
          <X size={14} weight="bold" />
        </button>

        {/* Header */}
        <div className="bg-[var(--primary)] text-white px-5 pt-5 pb-6 text-center relative">
          <div className="flex items-center justify-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={20} weight="fill" className="text-[var(--accent-sand)]" />
            ))}
          </div>
          <h2 id="review-nudge-title" className="font-heading font-black text-xl leading-tight">
            5 rounds in — how are we doing?
          </h2>
          <p className="text-sm opacity-90 mt-1 leading-snug">
            A quick honest answer helps me keep building Fairway.
          </p>
        </div>

        {/* Two paths */}
        <div className="px-4 py-4 space-y-2.5">
          <button
            type="button"
            onClick={onLoveIt}
            data-testid="review-nudge-loving"
            className="w-full rounded-2xl border-2 border-[var(--primary)] bg-[var(--primary)] text-white px-4 py-3 text-left flex items-center gap-3 hover:opacity-95 active:scale-[0.99] transition-transform"
          >
            <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              {storeUrl
                ? <Star size={20} weight="fill" />
                : <ShareNetwork size={20} weight="fill" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-black text-sm leading-tight">
                {storeUrl
                  ? (isIOS ? "Loving it — rate on the App Store" : "Loving it — rate on Play Store")
                  : "Loving it — share with a mate"}
              </div>
              <div className="text-[11px] opacity-85 mt-0.5">
                {storeUrl ? "Opens the listing in a new tab — takes 10s" : "Sends the link via WhatsApp / Messages"}
              </div>
            </div>
          </button>

          <Link
            to="/app/profile?contact=1"
            onClick={() => { markDismissed(); onClose?.(); }}
            data-testid="review-nudge-feedback"
            className="block w-full rounded-2xl border-2 border-[var(--accent-terracotta)]/40 bg-[var(--accent-terracotta)]/10 px-4 py-3 text-left flex items-center gap-3 hover:bg-[var(--accent-terracotta)]/15 transition-colors"
          >
            <div className="h-10 w-10 rounded-xl bg-[var(--accent-terracotta)] text-white flex items-center justify-center shrink-0">
              <EnvelopeSimple size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-black text-sm leading-tight text-[var(--primary)]">
                Something&apos;s broken — tell me
              </div>
              <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-snug">
                Private message straight to me. I read every one and reply personally.
              </div>
            </div>
          </Link>

          <button
            type="button"
            onClick={onSnooze}
            data-testid="review-nudge-later"
            className="w-full rounded-2xl bg-transparent text-[var(--text-secondary)] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:text-[var(--primary)] flex items-center justify-center gap-1.5"
          >
            <Clock size={12} weight="bold" />
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
